import "./lib/i18n/i18n";
import "./lib/sentry";
import "./main.css";

import * as Sentry from "@sentry/react";
import { lazy, StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router";
import AppErrorBoundary from "./app/app.error-boundary.tsx";
import AppLayout from "./app/app.layout.tsx";
import { StaffOnly } from "./lib/auth/auth.staff-only.tsx";
import ChatLayout from "./modules/chat/chat.layout.tsx";
import { log } from "@soulbyte/core";
import settingsRoutes from "./modules/settings/settings.routes";
import legalRoutes from "./app/legal/legal.routes";
import {
  LegacyModuleSettingsRedirect,
  ModuleLibraryRedirect,
} from "./modules/assistants/pages/assistant/assistant.routes";

const App = lazy(() => import("./app/app.tsx"));
const Chat = lazy(() => import("./modules/chat/chat.tsx"));
const OneDriveCallback = lazy(
  () => import("./lib/integrations/onedrive/pages/onedrive-callback.tsx"),
);
const AnalyticsPage = lazy(() => import("./modules/analytics"));
const VoiceCallsPage = lazy(() => import("./modules/voice-calls/voice-calls"));
const KnowledgeGraphPage = lazy(
  () => import("./modules/knowledge-graph/knowledge-graph.tsx"),
);

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const router = sentryCreateBrowserRouter([
  {
    element: <AppLayout />,
    // AppLayout mounts auth, theme, and tenant providers before <Outlet/> ever
    // renders — errors from those (e.g. tenant not provisioned yet) throw
    // above the "/" route's own errorElement, so this one has to catch them too
    errorElement: <AppErrorBoundary />,
    children: [
      {
        path: "/",
        element: <App />,
        errorElement: <AppErrorBoundary />,
        children: [
          {
            index: true,
            element: <Navigate to="/chat" replace />, // Redirect to chat page and replace the current history entry
          },
          {
            path: "chat",
            element: <ChatLayout />,
            children: [
              {
                index: true,
                element: <Chat />,
              },
            ],
          },
          {
            path: "assistants",
            children: [
              {
                // the library is a palette now, so /assistants only redirects
                index: true,
                element: <ModuleLibraryRedirect />,
              },
              {
                // Org skills library is retired from primary UX; deep links land
                // on the module library redirect so users are not stranded.
                path: "skills",
                element: <Navigate to="/assistants" replace />,
              },
              {
                path: ":assistantId/*",
                element: <LegacyModuleSettingsRedirect />,
              },
            ],
          },
          ...settingsRoutes,
          {
            path: "analytics",
            element: (
              <StaffOnly>
                <AnalyticsPage />
              </StaffOnly>
            ),
          },
          {
            // gated inside: voice.inspector or superadmin, like the inspector
            path: "calls",
            element: <VoiceCallsPage />,
          },
          {
            path: "graph",
            element: <KnowledgeGraphPage />,
          },
        ],
      },
      // frontegg finishes login on its oauth callback paths. render null so the
      // provider owns them — a 404 or navigating away wipes the code and login
      // fails. /oauth/account/* covers the social-login success after google
      // (/oauth/account/social/success); onedrive has its own routes below.
      {
        path: "/oauth/callback",
        element: null,
      },
      {
        path: "/oauth/account/*",
        element: null,
      },
      // Keeps AppLayout (and the Frontegg provider) mounted so the embedded login box renders
      {
        path: "/account/*",
        element: null,
      },
    ],
  },
  // Public legal pages, mounted outside the auth gate
  ...legalRoutes,
  // Integration callback routes
  {
    path: "/oauth/onedrive/*",
    element: <OneDriveCallback />,
  },
  {
    path: "/auth/onedrive/*",
    element: <OneDriveCallback />,
  },
]);

const container = document.getElementById("root");
const root = createRoot(container!, {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    log.error(error, {
      tags: {
        error_type: "react",
        error_code: "UNCAUGHT_ERROR",
      },
      extra: {
        componentStack: errorInfo.componentStack,
      },
      breadcrumbs: [
        {
          data: { componentStack: errorInfo.componentStack },
          level: "error",
          message: "Uncaught React error",
          category: "react",
        },
      ],
    });
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    log.error(error, {
      tags: {
        error_type: "react",
        error_code: "CAUGHT_ERROR",
      },
      extra: {
        componentStack: errorInfo.componentStack,
      },
      breadcrumbs: [
        {
          data: { componentStack: errorInfo.componentStack },
          level: "error",
          message: "React error caught by ErrorBoundary",
          category: "react",
        },
      ],
    });
  }),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler((error, errorInfo) => {
    log.error(error, {
      tags: {
        error_type: "react",
        error_code: "RECOVERABLE_ERROR",
      },
      extra: {
        componentStack: errorInfo.componentStack,
      },
      breadcrumbs: [
        {
          data: { componentStack: errorInfo.componentStack },
          level: "warning",
          message: "React recoverable error",
          category: "react",
        },
      ],
    });
  }),
});
root.render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);
